import React from 'react';
import { Menu, MenuItemProps } from 'semantic-ui-react';
import { Translate } from 'react-redux-i18n';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { systemSettingsMenuItems, systemSettingsRoutes, i18nKeys } from './constants';
import { isOwner, isSystemReadWrite, Session } from '../../modules/session';

const SystemSettingsMenu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSelector((state: any) => state.session) as Session;
  const showUrlUnlocking = isOwner(session);
  const showDisableOrganization = isOwner(session);
  // Charging requires a system admin credential, not owner specifically.
  const showBulkChargeGuarantee = isSystemReadWrite(session);
  // Adding creates event campaigns, so viewers are not offered the screen.
  const showAddOrganizationToEvents = isSystemReadWrite(session);
  // Publishing a version is a write, so viewers are not offered the screen.
  const showConsentDocumentVersions = isSystemReadWrite(session);
  const showGiftMatchingPools = isSystemReadWrite(session);

  const isActive = (menuItem: string) => {
    const path = location.pathname;
    switch (menuItem) {
      case systemSettingsMenuItems.SYSTEM_HEALTH:
        return path.includes(systemSettingsRoutes.HEALTH);
      case systemSettingsMenuItems.FRANCHISES:
        return path.includes(systemSettingsRoutes.FRANCHISES);
      case systemSettingsMenuItems.CAMPAIGN_ORGANIZERS:
        return path.includes(systemSettingsRoutes.CAMPAIGN_ORGANIZERS);
      case systemSettingsMenuItems.EVENT_ORGANIZERS:
        return path.includes(systemSettingsRoutes.EVENT_ORGANIZERS);
      case systemSettingsMenuItems.ACCESS_KEYS:
        return path.includes(systemSettingsRoutes.ACCESS_KEYS);
      case systemSettingsMenuItems.AUDIT_TRAIL:
        return path.includes(systemSettingsRoutes.AUDIT_TRAIL);
      case systemSettingsMenuItems.URL_UNLOCKING:
        return path.includes(systemSettingsRoutes.URL_UNLOCKING);
      case systemSettingsMenuItems.DISABLE_ORGANIZATION:
        return path.includes(systemSettingsRoutes.DISABLE_ORGANIZATION);
      case systemSettingsMenuItems.BULK_CHARGE_GUARANTEE:
        return path.includes(systemSettingsRoutes.BULK_CHARGE_GUARANTEE);
      case systemSettingsMenuItems.ADD_ORGANIZATION_TO_EVENTS:
        return path.includes(systemSettingsRoutes.ADD_ORGANIZATION_TO_EVENTS);
      case systemSettingsMenuItems.CONSENT_DOCUMENT_VERSIONS:
        return path.includes(systemSettingsRoutes.CONSENT_DOCUMENT_VERSIONS);
      case systemSettingsMenuItems.GIFT_MATCHING_POOLS:
        return path.includes(systemSettingsRoutes.GIFT_MATCHING_POOLS);
      default:
        return false;
    }
  };

  const handleItemClick = (_e: React.MouseEvent, data: MenuItemProps) => {
    const name = data.name;
    switch (name) {
      case systemSettingsMenuItems.SYSTEM_HEALTH:
        navigate(systemSettingsRoutes.HEALTH);
        break;
      case systemSettingsMenuItems.FRANCHISES:
        navigate(systemSettingsRoutes.FRANCHISES);
        break;
      case systemSettingsMenuItems.CAMPAIGN_ORGANIZERS:
        navigate(systemSettingsRoutes.CAMPAIGN_ORGANIZERS);
        break;
      case systemSettingsMenuItems.EVENT_ORGANIZERS:
        navigate(systemSettingsRoutes.EVENT_ORGANIZERS);
        break;
      case systemSettingsMenuItems.ACCESS_KEYS:
        navigate(systemSettingsRoutes.ACCESS_KEYS);
        break;
      case systemSettingsMenuItems.AUDIT_TRAIL:
        navigate(`${systemSettingsRoutes.AUDIT_TRAIL}?allRecords=true`);
        break;
      case systemSettingsMenuItems.URL_UNLOCKING:
        navigate(systemSettingsRoutes.URL_UNLOCKING);
        break;
      case systemSettingsMenuItems.DISABLE_ORGANIZATION:
        navigate(systemSettingsRoutes.DISABLE_ORGANIZATION);
        break;
      case systemSettingsMenuItems.BULK_CHARGE_GUARANTEE:
        navigate(systemSettingsRoutes.BULK_CHARGE_GUARANTEE);
        break;
      case systemSettingsMenuItems.ADD_ORGANIZATION_TO_EVENTS:
        navigate(systemSettingsRoutes.ADD_ORGANIZATION_TO_EVENTS);
        break;
      case systemSettingsMenuItems.CONSENT_DOCUMENT_VERSIONS:
        navigate(systemSettingsRoutes.CONSENT_DOCUMENT_VERSIONS);
        break;
      case systemSettingsMenuItems.GIFT_MATCHING_POOLS:
        navigate(systemSettingsRoutes.GIFT_MATCHING_POOLS);
        break;
    }
  };

  return (
    <Menu vertical fluid>
      <Menu.Item
        name={systemSettingsMenuItems.SYSTEM_HEALTH}
        active={isActive(systemSettingsMenuItems.SYSTEM_HEALTH)}
        as="a"
        onClick={handleItemClick}
      >
        <Translate value={i18nKeys.MENU.SYSTEM_HEALTH} />
      </Menu.Item>
      <Menu.Item
        name={systemSettingsMenuItems.FRANCHISES}
        active={isActive(systemSettingsMenuItems.FRANCHISES)}
        as="a"
        onClick={handleItemClick}
      >
        <Translate value={i18nKeys.MENU.FRANCHISES} />
      </Menu.Item>
      <Menu.Item
        name={systemSettingsMenuItems.CAMPAIGN_ORGANIZERS}
        active={isActive(systemSettingsMenuItems.CAMPAIGN_ORGANIZERS)}
        as="a"
        onClick={handleItemClick}
      >
        <Translate value={i18nKeys.MENU.CAMPAIGN_ORGANIZERS} />
      </Menu.Item>
      <Menu.Item
        name={systemSettingsMenuItems.EVENT_ORGANIZERS}
        active={isActive(systemSettingsMenuItems.EVENT_ORGANIZERS)}
        as="a"
        onClick={handleItemClick}
      >
        <Translate value={i18nKeys.MENU.EVENT_ORGANIZERS} />
      </Menu.Item>
      <Menu.Item
        name={systemSettingsMenuItems.ACCESS_KEYS}
        active={isActive(systemSettingsMenuItems.ACCESS_KEYS)}
        as="a"
        onClick={handleItemClick}
      >
        <Translate value={i18nKeys.MENU.ACCESS_KEYS} />
      </Menu.Item>
      {showUrlUnlocking && (
        <Menu.Item
          name={systemSettingsMenuItems.URL_UNLOCKING}
          active={isActive(systemSettingsMenuItems.URL_UNLOCKING)}
          as="a"
          onClick={handleItemClick}
        >
          <Translate value={i18nKeys.MENU.URL_UNLOCKING} />
        </Menu.Item>
      )}
      {showDisableOrganization && (
        <Menu.Item
          name={systemSettingsMenuItems.DISABLE_ORGANIZATION}
          active={isActive(systemSettingsMenuItems.DISABLE_ORGANIZATION)}
          as="a"
          onClick={handleItemClick}
        >
          <Translate value={i18nKeys.MENU.DISABLE_ORGANIZATION} />
        </Menu.Item>
      )}
      {showBulkChargeGuarantee && (
        <Menu.Item
          name={systemSettingsMenuItems.BULK_CHARGE_GUARANTEE}
          active={isActive(systemSettingsMenuItems.BULK_CHARGE_GUARANTEE)}
          as="a"
          onClick={handleItemClick}
        >
          <Translate value={i18nKeys.MENU.BULK_CHARGE_GUARANTEE} />
        </Menu.Item>
      )}
      {showAddOrganizationToEvents && (
        <Menu.Item
          name={systemSettingsMenuItems.ADD_ORGANIZATION_TO_EVENTS}
          active={isActive(systemSettingsMenuItems.ADD_ORGANIZATION_TO_EVENTS)}
          as="a"
          onClick={handleItemClick}
        >
          <Translate value={i18nKeys.MENU.ADD_ORGANIZATION_TO_EVENTS} />
        </Menu.Item>
      )}
      {showConsentDocumentVersions && (
        <Menu.Item
          name={systemSettingsMenuItems.CONSENT_DOCUMENT_VERSIONS}
          active={isActive(systemSettingsMenuItems.CONSENT_DOCUMENT_VERSIONS)}
          as="a"
          onClick={handleItemClick}
        >
          <Translate value={i18nKeys.MENU.CONSENT_DOCUMENT_VERSIONS} />
        </Menu.Item>
      )}
      {showGiftMatchingPools && (
        <Menu.Item
          name={systemSettingsMenuItems.GIFT_MATCHING_POOLS}
          active={isActive(systemSettingsMenuItems.GIFT_MATCHING_POOLS)}
          as="a"
          onClick={handleItemClick}
        >
          <Translate value={i18nKeys.MENU.GIFT_MATCHING_POOLS} />
        </Menu.Item>
      )}
      <Menu.Item
        name={systemSettingsMenuItems.AUDIT_TRAIL}
        active={isActive(systemSettingsMenuItems.AUDIT_TRAIL)}
        as="a"
        onClick={handleItemClick}
      >
        <Translate value={i18nKeys.MENU.AUDIT_TRAIL} />
      </Menu.Item>
    </Menu>
  );
};

export default SystemSettingsMenu;
